import { observer } from 'mobx-react-lite'
import React from 'react'
import { useNavigate, useParams } from 'react-router'

// evertel
import { Modal, ModalBody, ModalHeader, Button, Text, Row, Col, useUI } from '@evertel/web/ui'
import { WidgetsProvider, Document } from '@evertel/schema-parser'
import { APIDataDocumentSchema } from '@evertel/types'
import { useService } from '@evertel/di'
import { CloneSchemaController } from '@evertel/document'
import { DocumentServiceProvider } from '@evertel/service'
import { DocumentIcon } from '../elements/DocumentIcon'
import { WriteWidgets } from '../schema-widgets'

interface Props {
    visible?: boolean
    schema: APIDataDocumentSchema
    onClose: () => void
    onUse: (schema: APIDataDocumentSchema) => void
    isLoading: boolean
}

const SchemaPreviewModal: React.FC<Props> = observer(({
    visible,
    schema,
    onClose,
    onUse,
    isLoading
}) => {

    const { target } = useParams()
    const navigate = useNavigate()
    const { addToast } = useUI()

    const cloneController = useService(CloneSchemaController, [])

    const onCloneSchema = async () => {
        const newName = schema.name + ' (copy)'

        try {
            const newSchema = await cloneController.cloneSchema(schema.id, 0, newName)
            onClose()
            setTimeout(() => navigate(`/everdocs/schema/edit/${newSchema.id}/global`))

        } catch (error: any) {
            addToast({
                color: 'danger',
                title: 'Clone Unsuccessful',
                message: error.message || error
            })
        }
    }

    return (
        <Modal
            visible={visible}
            onClose={(isLoading) ? undefined : onClose}
            size="xl">
            <ModalHeader
                closeButton={!isLoading}>
                {/* Row/Col here are plain flex, not the bootstrap grid: every Col grows equally
                    unless told otherwise, so pin the icon and actions and let the title take
                    the remaining width */}
                <Row
                    valign="center"
                    className="w-100">
                    <Col
                        xs="auto"
                        className="flex-grow-0 me-3">
                        <DocumentIcon
                            schema={schema}
                            width={60}
                            height={60}
                        />
                    </Col>
                    <Col
                        align="left"
                        className="min-w-0">
                        <Text
                            tag="h3"
                            bold>
                            {schema?.name}
                        </Text>
                        <Text>
                            {schema?.description}
                        </Text>
                    </Col>
                    <Col
                        xs="auto"
                        align="right"
                        className="flex-grow-0">
                        {(target === 'global') ?
                            <Row>
                                <Button
                                    onClick={onCloneSchema}
                                    color="link"
                                    className="me-5">
                                    <Text>
                                        Clone this Template
                                    </Text>
                                </Button>
                                <Button
                                    onClick={() => navigate(`/everdocs/schema/edit/${schema.id}/global`)}
                                    color="warning"
                                    className="me-3">
                                    <Text>
                                        Edit this Template
                                    </Text>
                                </Button>
                            </Row>
                            :
                            <Button
                                onClick={() => onUse(schema as APIDataDocumentSchema)}
                                color="primary"
                                className="me-3"
                                loading={isLoading}>
                                <Text>
                                    Use this Template
                                </Text>
                            </Button>
                        }
                    </Col>
                </Row>
            </ModalHeader>
            <ModalBody className="p-5">
                <DocumentServiceProvider services={[{ id: 'meta', value: { } }]}>
                    <WidgetsProvider value={WriteWidgets}>
                        {(schema?.schema) && (
                            <Document
                                layout="full"
                                schemaId={schema.id}
                                schema={schema?.schema as any}
                            />
                        )}
                    </WidgetsProvider>
                </DocumentServiceProvider>
            </ModalBody>
        </Modal>
    )
})

export {
    SchemaPreviewModal
}
